import type {
  CalculationResponse,
  Quantity,
  ValidationMessage,
  ZoneResult,
} from '../../gen/ejot/roofing/v1/types';

interface CsvRow {
  section: string;
  key: string;
  moduleId?: string;
  zoneId?: string;
  value?: string | number;
  unit?: string;
  status?: string;
  resistanceBasis?: string;
  source?: string;
}

const HEADERS = ['section','key','module_id','zone_id','value','unit','status','resistance_basis','source'] as const;

export function buildCalculationCsv(result: CalculationResponse, applicationVersion: string): string {
  const rows: CsvRow[] = [];
  const versions = result.versions;
  rows.push(
    metadata('application_version', applicationVersion),
    metadata('engine_version', versions?.engine_version),
    metadata('catalogue_version', versions?.catalogue_version),
    metadata('engineering_dataset_version', versions?.engineering_dataset_version),
    metadata('project_schema_version', versions?.project_schema_version),
    metadata('calculation_status', result.status),
    metadata('calculated_at', result.calculated_at),
    metadata('input_hash', result.input_hash),
    metadata('development_data_used', String(Boolean(result.development_data_used))),
    metadata('demonstration_result', String(Boolean(result.demonstration_result))),
  );
  appendMessages(rows, result.messages ?? [], 'project-message');

  for (const module of result.modules ?? []) {
    const moduleId = module.module_id ?? '';
    rows.push({ section:'module', key:'status', moduleId, value:module.status ?? '', status:module.status ?? '', resistanceBasis:module.resistance_basis ?? '' });
    rows.push({ section:'module', key:'governing_zone_id', moduleId, value:module.governing_zone_id ?? '', status:module.status ?? '', resistanceBasis:module.resistance_basis ?? '' });
    rows.push({ section:'module', key:'governing_resistance_mode', moduleId, value:module.governing_resistance_mode ?? '', status:module.status ?? '', resistanceBasis:module.resistance_basis ?? '' });
    if (module.highest_utilization) rows.push(quantityRow('module','highest_utilization',module.highest_utilization,{moduleId,status:module.status,resistanceBasis:module.resistance_basis}));
    for (const zone of module.zones ?? []) appendZone(rows, moduleId, zone);
    appendQuantitySummary(rows,'module-quantity',moduleId,module.quantities);
    appendMessages(rows,module.messages ?? [],'module-message',moduleId);
  }
  appendQuantitySummary(rows,'project-quantity',undefined,result.project_quantities);
  return [HEADERS.join(','), ...rows.map(serializeRow)].join('\r\n') + '\r\n';
}

export function downloadCalculationCsv(result: CalculationResponse, applicationVersion: string, filename='roofing-calculation.csv'): void {
  const blob = new Blob([buildCalculationCsv(result, applicationVersion)], {type:'text/csv;charset=utf-8'});
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href=url; anchor.download=filename; anchor.style.display='none'; document.body.appendChild(anchor); anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
}

function appendZone(rows: CsvRow[], moduleId: string, zone: ZoneResult): void {
  const common={moduleId,zoneId:zone.zone_id,status:zone.status,resistanceBasis:zone.resistance_basis};
  rows.push({section:'zone',key:'display_label',...common,value:zone.display_label ?? ''});
  if (zone.area) rows.push({section:'zone',key:'area',...common,value:zone.area.square_metres ?? '',unit:'m2'});
  appendQuantity(rows,'zone','external_pressure_coefficient',zone.external_pressure_coefficient,common);
  appendQuantity(rows,'zone','characteristic_pressure',zone.characteristic_pressure,common);
  appendQuantity(rows,'zone','design_pressure',zone.design_pressure,common);
  appendQuantity(rows,'zone','governing_characteristic_resistance',zone.governing_characteristic_resistance,common);
  appendQuantity(rows,'zone','governing_design_resistance',zone.governing_design_resistance,common);
  appendQuantity(rows,'zone','utilization',zone.utilization,common);

  const etaPullOutBasis=zone.resistance_basis === 'FIXING_RESISTANCE_BASIS_ETA_COMPONENT_CHARACTERISTIC';
  appendQuantity(rows,'zone-layout','theoretical_density',zone.layout?.theoretical_density,common);
  appendQuantity(rows,'zone-layout','installable_density',zone.layout?.installable_density,common);
  if (!etaPullOutBasis && zone.layout?.spacing_in_overlap) {
    rows.push({section:'zone-layout',key:'spacing_in_overlap',...common,value:zone.layout.spacing_in_overlap.millimetres ?? '',unit:'mm'});
  }
  if (zone.layout?.total_fixings !== undefined) rows.push({section:'zone-layout',key:'total_fixings',...common,value:zone.layout.total_fixings,unit:'pcs'});
  for (const resistance of zone.resistances ?? []) {
    const suffix = resistance.resistance_dataset_id || resistance.mode || 'resistance';
    appendQuantity(rows,'resistance',`${suffix}.characteristic`,resistance.characteristic_resistance,common);
    appendQuantity(rows,'resistance',`${suffix}.partial_factor`,resistance.partial_factor,common);
    appendQuantity(rows,'resistance',`${suffix}.design`,resistance.design_resistance,common);
  }
  appendMessages(rows,zone.messages ?? [],'zone-message',moduleId,zone.zone_id);
}

function appendQuantitySummary(rows: CsvRow[], section: string, moduleId: string|undefined, summary: CalculationResponse['project_quantities']): void {
  if (!summary) return;
  if (summary.total_membrane_area) rows.push({section,key:'total_membrane_area',moduleId,value:summary.total_membrane_area.square_metres ?? '',unit:'m2'});
  rows.push(
    {section,key:'estimated_membrane_rolls',moduleId,value:summary.estimated_membrane_rolls ?? 0,unit:'rolls'},
    {section,key:'total_fixings',moduleId,value:summary.total_fixings ?? 0,unit:'pcs'},
    {section,key:'total_insulation_fixings',moduleId,value:summary.total_insulation_fixings ?? 0,unit:'pcs'},
    {section,key:'total_perimeter_fixings',moduleId,value:summary.total_perimeter_fixings ?? 0,unit:'pcs'},
    {section,key:'waste_factor_percent',moduleId,value:summary.waste_factor_percent ?? 0,unit:'%'},
  );
  for (const line of summary.lines ?? []) rows.push({section:`${section}-bom`,key:line.product_id || line.display_name || 'item',moduleId,value:line.quantity ?? 0,unit:line.unit ?? '',source:`${line.display_name ?? ''}${line.development_only ? ' [DEVELOPMENT_ONLY]' : ''}`});
}

function appendMessages(rows: CsvRow[], messages: readonly ValidationMessage[], section: string, moduleId?: string, zoneId?: string): void {
  for (const message of messages) rows.push({section,key:message.code || 'message',moduleId,zoneId,value:message.title ?? '',status:message.severity ?? '',source:message.reference?.display_text || message.explanation || ''});
}
function appendQuantity(rows: CsvRow[], section: string, key: string, quantity: Quantity|undefined, context: Partial<CsvRow>): void { if (quantity) rows.push(quantityRow(section,key,quantity,context)); }
function quantityRow(section:string,key:string,quantity:Quantity,context:Partial<CsvRow>):CsvRow{return{section,key,...context,value:quantity.value ?? '',unit:quantity.unit ?? '',source:quantity.reference?.display_text || quantity.reference?.reference_id || ''};}
function metadata(key:string,value:unknown):CsvRow{return{section:'metadata',key,value:value===undefined||value===null?'':String(value)};}
function serializeRow(row:CsvRow):string{return[row.section,row.key,row.moduleId,row.zoneId,row.value,row.unit,row.status,row.resistanceBasis,row.source].map(csvCell).join(',');}
function csvCell(value:unknown):string{const text=value===undefined||value===null?'':String(value);return /[",\r\n]/.test(text)?`"${text.replace(/"/g,'""')}"`:text;}
